import { isoDate } from "./format";

/**
 * Quick date-range presets for list filters — the same set the SalePro admin offers in its
 * `DateRangePicker` footer (Hôm nay … Năm nay), so staff who use both products meet one vocabulary.
 *
 * Ranges are inclusive ISO `yyyy-MM-dd` pairs in local time. Weeks start on Monday (Vietnamese
 * convention; a Sunday belongs to the week that began the Monday before it).
 */
export type DatePresetKey =
  | "TODAY"
  | "YESTERDAY"
  | "THIS_WEEK"
  | "LAST_WEEK"
  | "THIS_MONTH"
  | "LAST_MONTH"
  | "THIS_YEAR";

export interface DateRange {
  from: string;
  to: string;
}

export const DATE_PRESETS: { key: DatePresetKey; label: string }[] = [
  { key: "TODAY", label: "Hôm nay" },
  { key: "YESTERDAY", label: "Hôm qua" },
  { key: "THIS_WEEK", label: "Tuần này" },
  { key: "LAST_WEEK", label: "Tuần trước" },
  { key: "THIS_MONTH", label: "Tháng này" },
  { key: "LAST_MONTH", label: "Tháng trước" },
  { key: "THIS_YEAR", label: "Năm nay" },
];

export const DATE_PRESET_LABEL: Record<DatePresetKey, string> = Object.fromEntries(
  DATE_PRESETS.map((p) => [p.key, p.label])
) as Record<DatePresetKey, string>;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Monday of the week containing `d`. */
function mondayOf(d: Date): Date {
  const day = startOfDay(d);
  // getDay(): 0 = Sunday … 6 = Saturday. Shift so Monday = 0.
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

export function presetRange(key: DatePresetKey, now: Date = new Date()): DateRange {
  const today = startOfDay(now);
  switch (key) {
    case "TODAY":
      return { from: isoDate(today), to: isoDate(today) };
    case "YESTERDAY": {
      const y = addDays(today, -1);
      return { from: isoDate(y), to: isoDate(y) };
    }
    case "THIS_WEEK": {
      const mon = mondayOf(today);
      return { from: isoDate(mon), to: isoDate(addDays(mon, 6)) };
    }
    case "LAST_WEEK": {
      const mon = addDays(mondayOf(today), -7);
      return { from: isoDate(mon), to: isoDate(addDays(mon, 6)) };
    }
    case "THIS_MONTH": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: isoDate(first), to: isoDate(last) };
    }
    case "LAST_MONTH": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: isoDate(first), to: isoDate(last) };
    }
    case "THIS_YEAR": {
      const first = new Date(today.getFullYear(), 0, 1);
      const last = new Date(today.getFullYear(), 11, 31);
      return { from: isoDate(first), to: isoDate(last) };
    }
  }
}

/** The preset whose range is exactly `from`..`to`, or null when the pair is a custom range. */
export function matchPreset(
  from: string | null | undefined,
  to: string | null | undefined,
  now: Date = new Date()
): DatePresetKey | null {
  if (!from || !to) return null;
  for (const p of DATE_PRESETS) {
    const r = presetRange(p.key, now);
    if (r.from === from && r.to === to) return p.key;
  }
  return null;
}

/** `yyyy-MM-dd` → `dd/MM/yyyy` without going through `Date` (so no time-zone surprises). */
export function formatIsoDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Human label for a range: the preset name when it matches one, otherwise `dd/MM/yyyy → dd/MM/yyyy`
 * (or an open-ended `Từ …` / `Đến …`). Null when both ends are empty.
 */
export function describeRange(
  from: string | null | undefined,
  to: string | null | undefined,
  now: Date = new Date()
): string | null {
  if (!from && !to) return null;
  const preset = matchPreset(from, to, now);
  if (preset) return DATE_PRESET_LABEL[preset];
  if (from && to) return `${formatIsoDate(from)} → ${formatIsoDate(to)}`;
  if (from) return `Từ ${formatIsoDate(from)}`;
  return `Đến ${formatIsoDate(to!)}`;
}

/** `yyyy-MM-dd` → local-midnight Date, or null when malformed. */
export function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}
